import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
} from 'firebase/firestore';
import type { Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { isAdminDomain } from '@/utils/adminAccess';
import type { UserPlan } from '@/utils/planLimits';

// User profile stored in Firestore
export interface UserProfile {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  plan: UserPlan;
  createdAt: Timestamp;
  lastLoginAt: Timestamp | null;
}

// User report item with aggregated counts
export interface UserReportItem {
  uid: string;
  email: string;
  displayName: string | null;
  plan: UserPlan;
  joinedAt: Timestamp;
  lastLoginAt: Timestamp | null;
  thinkSpaceCount: number;
  todoListCount: number;
  notebookCount: number;
  storageUsedBytes: number;
}

const USERS_COLLECTION = 'users';
const THINKSPACES_COLLECTION = 'thinkspaces';
const TODOLISTS_COLLECTION = 'todolists';
const NOTEBOOKS_COLLECTION = 'notebooks';

export const adminService = {
  /**
   * Check if user has admin access based on email domain
   */
  checkAdminAccess: (email: string | null | undefined): boolean => {
    return isAdminDomain(email);
  },

  /**
   * Get user report with aggregated counts
   * Only accessible to admin domain users
   */
  getUserReport: async (adminEmail: string): Promise<UserReportItem[]> => {
    // Verify admin access
    if (!isAdminDomain(adminEmail)) {
      throw new Error('Unauthorized: Admin access required');
    }

    // Get all users
    const usersSnapshot = await getDocs(collection(db, USERS_COLLECTION));
    const users = usersSnapshot.docs.map((d) => ({
      uid: d.id,
      ...d.data(),
    })) as (UserProfile & { uid: string })[];

    // Get all content collections to count ownership
    const [spacesSnapshot, todosSnapshot, notebooksSnapshot] = await Promise.all([
      getDocs(collection(db, THINKSPACES_COLLECTION)),
      getDocs(collection(db, TODOLISTS_COLLECTION)),
      getDocs(collection(db, NOTEBOOKS_COLLECTION)),
    ]);

    // Build ownership counts by userId
    const spaceCountsByUser = new Map<string, number>();
    spacesSnapshot.docs.forEach((d) => {
      const ownerId = d.data().ownerId as string;
      spaceCountsByUser.set(ownerId, (spaceCountsByUser.get(ownerId) || 0) + 1);
    });

    const todoCountsByUser = new Map<string, number>();
    todosSnapshot.docs.forEach((d) => {
      const ownerId = d.data().ownerId as string;
      todoCountsByUser.set(ownerId, (todoCountsByUser.get(ownerId) || 0) + 1);
    });

    const notebookCountsByUser = new Map<string, number>();
    notebooksSnapshot.docs.forEach((d) => {
      const ownerId = d.data().ownerId as string;
      notebookCountsByUser.set(ownerId, (notebookCountsByUser.get(ownerId) || 0) + 1);
    });

    // Calculate storage per user (from thinkspace image elements)
    const storageByUser = new Map<string, number>();
    spacesSnapshot.docs.forEach((d) => {
      const data = d.data();
      const ownerId = data.ownerId as string;
      const elements = data.elements as Array<{ type?: string; dataUrl?: string }> | undefined;

      if (elements) {
        let bytes = 0;
        elements.forEach((el) => {
          if (el.type === 'image' && el.dataUrl) {
            // Estimate bytes from base64 data URL
            // Base64 is ~4/3 the size of the original binary
            const base64Length = el.dataUrl.length - (el.dataUrl.indexOf(',') + 1);
            bytes += Math.ceil((base64Length * 3) / 4);
          }
        });
        storageByUser.set(ownerId, (storageByUser.get(ownerId) || 0) + bytes);
      }
    });

    // Build report items
    const report: UserReportItem[] = users.map((user) => ({
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      plan: user.plan || 'free',
      joinedAt: user.createdAt,
      lastLoginAt: user.lastLoginAt,
      thinkSpaceCount: spaceCountsByUser.get(user.uid) || 0,
      todoListCount: todoCountsByUser.get(user.uid) || 0,
      notebookCount: notebookCountsByUser.get(user.uid) || 0,
      storageUsedBytes: storageByUser.get(user.uid) || 0,
    }));

    // Sort by join date (newest first)
    report.sort((a, b) => {
      const aTime = a.joinedAt?.toMillis?.() || 0;
      const bTime = b.joinedAt?.toMillis?.() || 0;
      return bTime - aTime;
    });

    return report;
  },

  /**
   * Update a user's plan
   * Only accessible to admin domain users
   */
  updateUserPlan: async (
    adminEmail: string,
    userId: string,
    newPlan: UserPlan
  ): Promise<void> => {
    // Verify admin access
    if (!isAdminDomain(adminEmail)) {
      throw new Error('Unauthorized: Admin access required');
    }

    const userRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(userRef, {
      plan: newPlan,
    });
  },

  /**
   * Get a single user's profile
   */
  getUserProfile: async (userId: string): Promise<UserProfile | null> => {
    const userRef = doc(db, USERS_COLLECTION, userId);
    const snapshot = await getDoc(userRef);
    if (!snapshot.exists()) return null;
    return { uid: snapshot.id, ...snapshot.data() } as UserProfile;
  },
};
